import { FC, ReactNode, useState } from 'react';

interface PaginationBarProps {
  page: number;
  totalPages: number;
  onPrev?: () => void;
  onNext?: () => void;
  onSelect?: (page: number) => void;
  disabled?: boolean;
  renderSummary?: ReactNode;
  renderExtra?: ReactNode;
}

const PaginationBar: FC<PaginationBarProps> = ({
  page,
  totalPages,
  onPrev,
  onNext,
  onSelect,
  disabled,
  renderSummary,
  renderExtra,
}) => {
  const [jumpValue, setJumpValue] = useState<string>('');
  const buildPageItems = (): Array<number | 'ellipsis'> => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const visible = new Set<number>();
    // Always show first 3 and last 3 pages
    for (let i = 1; i <= Math.min(3, totalPages); i++) {
      visible.add(i);
    }
    for (let i = Math.max(totalPages - 2, 1); i <= totalPages; i++) {
      visible.add(i);
    }
    // Show current page and one neighbour on each side
    for (let i = page - 1; i <= page + 1; i++) {
      if (i >= 1 && i <= totalPages) {
        visible.add(i);
      }
    }

    const sorted = Array.from(visible).sort((a, b) => a - b);
    const result: Array<number | 'ellipsis'> = [];
    for (let i = 0; i < sorted.length; i++) {
      const current = sorted[i];
      const prev = sorted[i - 1];
      if (i > 0 && prev !== undefined && current - prev > 1) {
        result.push('ellipsis');
      }
      result.push(current);
    }
    return result;
  };

  const pageItems = buildPageItems();

  return (
    <div className="mt-6 flex flex-col gap-4 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 overflow-x-auto rounded-full border border-slate-200 bg-slate-50 px-3 py-1 shadow-inner">
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:text-slate-300"
          disabled={disabled || page <= 1}
          onClick={onPrev}
        >
          ‹
        </button>
        {pageItems.map((item, index) =>
          item === 'ellipsis' ? (
            <span
              key={`el-${index}`}
              className="flex h-8 w-8 items-center justify-center text-xs text-slate-400"
            >
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm transition ${item === page
                  ? 'bg-primary text-white shadow'
                  : 'text-slate-600 hover:bg-primary/10 hover:text-primary'
                }`}
              disabled={disabled}
              onClick={() => onSelect?.(item)}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:text-slate-300"
          disabled={disabled || page >= totalPages}
          onClick={onNext}
        >
          ›
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center text-xs text-slate-500">
        {renderSummary}
      </div>

      <div className="flex items-center justify-end gap-3 text-sm text-slate-600">
        {renderExtra}
        {totalPages > 1 && (
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <span>Go to</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={jumpValue}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  setJumpValue('');
                  return;
                }
                if (!/^\d+$/.test(v)) return;
                setJumpValue(v);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && jumpValue && onSelect) {
                  const num = Number(jumpValue);
                  if (Number.isFinite(num)) {
                    const clamped = Math.min(totalPages, Math.max(1, num));
                    onSelect(clamped);
                  }
                }
              }}
              className="h-7 w-14 rounded-full border border-slate-300 bg-white px-2 text-xs text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/40"
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaginationBar;
